/**
 * isolateColor.js
 *
 * Resolves a 3D-isolation paint colour for a tag's colouring value, from whichever colouring
 * SOURCE the widget uses. The datagrid "Isolate All" and every chart-selection isolate share
 * this one resolver, so an isolated element paints exactly the colour the chart fill would give
 * it - and, for a registry schema, follows live schema edits instead of a frozen value->hex map.
 *
 * A `coloringSpec` is one of:
 *   { mode: 'schema', schemaSelection }  - resolve each value through the live schema (registry /
 *                                          builtin), the same `resolveSchema` the chart fill uses.
 *   { mode: 'static', valueColors }      - the legacy per-widget value->hex map (Link-3D wizard).
 *   { mode: 'auto', valueColors? }       - un-schemed dimension: the chart's rendered fill for any
 *                                          value it painted, else a rank-stable categorical colour.
 *   null / undefined                     - no colouring; the caller does a plain isolate.
 *
 * PURE module: no Vuex, no components.
 */

import { resolveSchema } from '@/features/Dashboard/utils/coloringSchemaEngine';
import { outcomeFill } from '@/features/Dashboard/charts/echartsOptions';
import { stableCategoryColor } from '@/features/Dashboard/charts/chartTheme';
import { toCategoryLabel } from '@/features/Dashboard/service/chart-data.service';
import { UNMAPPED_ISOLATE_COLOR } from '@/utils/forgeUtils';

/**
 * Build a `(value) => hex` colour function for a coloringSpec, or null for a plain (uncoloured)
 * isolate. The returned function is total: an unmapped / no-data value yields UNMAPPED_ISOLATE_COLOR
 * (neutral grey) so the element stays visible instead of vanishing from the isolate.
 *
 * @param {{mode:'schema', schemaSelection:object}|{mode:'static', valueColors:object}|null} coloringSpec
 * @param {{ librarySchemas?: Array, theme?: string }} [context] - librarySchemas resolves 'reference'
 *        schema selections; theme ('light'|'dark') drives tone->hex, sourced the same way the chart
 *        fill does ($vuetify.theme.dark). Both are needed only for mode 'schema'.
 * @returns {((value:*) => string) | null}
 */
export function makeIsolateColorResolver(coloringSpec, context = {}) {
  if (!coloringSpec) return null;

  if (coloringSpec.mode === 'schema' && coloringSpec.schemaSelection) {
    const { librarySchemas, theme } = context;
    const lookup = resolveSchema(coloringSpec.schemaSelection, { librarySchemas, theme });
    return (value) => outcomeFill(lookup(toCategoryLabel(value)), theme) || UNMAPPED_ISOLATE_COLOR;
  }

  if (coloringSpec.mode === 'static' && coloringSpec.valueColors) {
    const valueColors = coloringSpec.valueColors;
    return (value) => valueColors[toCategoryLabel(value)]
      || valueColors[String(value)]
      || UNMAPPED_ISOLATE_COLOR;
  }

  if (coloringSpec.mode === 'auto') {
    // No scheme for this dimension: value-stable categorical colour by RANK in the value set (passed
    // as context.autoValues = the chart's OWN prepareChartData().labels). Rank AND key exactly as the
    // chart fill does — the same stableCategoryColor over the same set, keyed through toCategoryLabel
    // (raw value → display label; null/blank → 'Unset', trimmed) — so a value's isolate colour is the
    // SAME hue the chart gives it, for every bucket including the null/Unset one.
    //
    // `valueColors` (optional) is the chart's ON-SCREEN fill map. Ranking reconstructs the hue, but
    // the rendered option is the only thing that KNOWS it: horizontal bars reverse their prepared
    // rows and schema fills are applied while building the option, so a reconstruction can drift.
    // Any value the chart actually painted takes its real fill; the rest rank exactly as before.
    const autoValues = context.autoValues;
    const valueColors = coloringSpec.valueColors || null;
    return (value) => {
      const label = toCategoryLabel(value);
      return (valueColors && valueColors[label]) || stableCategoryColor(label, autoValues);
    };
  }

  return null;
}
